import React, {useCallback, useContext} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {deleteRegisteredBrowser} from 'src/openapi/admin/apis/DeleteRegisteredBrowserApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import IconButton from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';
import {useShown} from '@pb/kit-hooks-use-shown';

import type {MessageContextType} from 'src/contexts/message-context';

export const RemoveBrowserController = ({userId, userPlaceId}: {userId: number, userPlaceId: string}) => {
    const {onClose, shown, onToggle} = useShown();

    const {setSuccessMessage, setErrorMessage} = useContext<MessageContextType>(MessageContext);

    const appDispatch = useAppDispatch();
    const {hasAccess} = useAdminAccessRight();

    const onRemove = useCallback(async () => {
        const res = await appDispatch(deleteRegisteredBrowser({
            userId,
            userPlaceId,
        }));

        if (res.payload.success) {
            setSuccessMessage('Registered browser was removed');
        }

        if (res.payload.error) {
            setErrorMessage(getError({error: res.payload.data}));
        }
        onClose();

    }, [userId, userPlaceId, appDispatch, setSuccessMessage, setErrorMessage, onClose]);

    return (
        <>
            <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                <IconButton
                    ref={anchorRef}
                    {...getAnchorProps()}
                    disabled={!hasAccess(ACCESS_RIGHTS.USER_SCREEN_EDIT)}
                    iconProps={{type: Icons.TRASH_LIGHT}}
                    onClick={onToggle}
                    flat={true}
                />
            )}>
                Delete
            </Tooltip>
            <Dialog
                shown={shown}
                setShown={onClose}
                wrapperProps={{style: {width: 360}}}>
                <DialogHeader onClose={onClose}>
                    Remove registered browser
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    <Group>
                        <Button variant="Type.PRIMARY" onClick={onRemove}>Remove</Button>
                        <Button variant="Type.SECONDARY" onClick={onClose}>Cancel</Button>
                    </Group>
                </DialogMain>
                <DialogFooter/>
            </Dialog>
        </>
    );
};
